import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { atualizarJson, enviarJson, enviarSemConteudo, pedirJson } from "./api";
import { esquemaTicket } from "./tickets";

/* WebChat: o chat do portal do cliente e a fila de quem atende. O contrato é o de
   `api/app/dominios/webchat/`. A conversa chega ao vivo por Server-Sent Events (o mesmo padrão da
   fila de chamados) e as ações vão por POST. */

export const CHAVE_DO_WEBCHAT = ["webchat"] as const;

const uuid = z.string().uuid();

/** De que lado da conversa a tela está: o caminho da API muda, o contrato da fala não. */
export type LadoDoChat = "cliente" | "atendimento";

export function caminhoDaConversa(lado: LadoDoChat, id: string): string {
  const base = lado === "cliente" ? "/webchat/conversas" : "/webchat/atendimento/conversas";
  return `${base}/${encodeURIComponent(id)}`;
}

// ------------------------------------------------------------------------------------- contratos

const esquemaMensagem = z.object({
  id: uuid,
  tipo: z.enum(["texto", "sistema"]),
  autor_tipo: z.enum(["cliente", "agente", "sistema"]),
  autor_nome: z.string().nullable(),
  texto: z.string(),
  criada_em: z.string(),
  fato: z.string().nullable().optional(),
});
export type MensagemDoChat = z.infer<typeof esquemaMensagem>;

const esquemaConversaDoCliente = z.object({
  id: uuid,
  estado: z.enum(["fila", "ativa", "encerrada"]),
  criada_em: z.string(),
  aceita_em: z.string().nullable(),
  encerrada_em: z.string().nullable(),
  atendente_nome: z.string().nullable(),
  fora_do_horario: z.boolean(),
  ticket_numero: z.number().int().nullable(),
  pode_avaliar: z.boolean(),
});
export type ConversaDoCliente = z.infer<typeof esquemaConversaDoCliente>;

const esquemaEstadoDoChat = z.object({
  disponivel: z.boolean(),
  aberto_agora: z.boolean(),
  mensagem_fora_do_horario: z.string().nullable(),
  proxima_abertura: z.string().nullable(),
  digitando_ativo: z.boolean(),
  conversa: esquemaConversaDoCliente.nullable(),
});
export type EstadoDoChat = z.infer<typeof esquemaEstadoDoChat>;

const esquemaConversaDoAtendimento = z.object({
  id: uuid,
  organizacao_id: uuid,
  organizacao_nome: z.string(),
  solicitante_nome: z.string().nullable(),
  equipe_id: uuid.nullable(),
  equipe_nome: z.string().nullable(),
  atendente_id: uuid.nullable(),
  atendente_nome: z.string().nullable(),
  estado: z.enum(["fila", "ativa", "encerrada"]),
  fora_do_horario: z.boolean(),
  criada_em: z.string(),
  aceita_em: z.string().nullable(),
  encerrada_em: z.string().nullable(),
  ultima_mensagem_em: z.string(),
  previa: z.string().nullable(),
  ticket_id: uuid.nullable(),
  ticket_numero: z.number().int().nullable(),
  motivo_encerramento: z.string().nullable(),
  avaliacao_nota: z.number().int().nullable(),
  avaliacao_comentario: z.string().nullable(),
});
export type ConversaDoAtendimento = z.infer<typeof esquemaConversaDoAtendimento>;

const esquemaResultadoDoEncerramento = z.object({
  conversa: esquemaConversaDoAtendimento,
  ticket_id: uuid,
  ticket_numero: z.number().int(),
});

const esquemaConfiguracao = z.object({
  organizacao_id: uuid,
  personalizada: z.boolean(),
  ativo: z.boolean(),
  calendario_id: uuid.nullable(),
  equipe_id: uuid.nullable(),
  mensagem_fora_do_horario: z.string().nullable(),
  digitando_ativo: z.boolean(),
  calendario_efetivo_id: uuid.nullable(),
  aberto_agora: z.boolean(),
  atualizado_em: z.string().nullable(),
});
export type ConfiguracaoDoChat = z.infer<typeof esquemaConfiguracao>;

// ------------------------------------------------------------------------------------ a conversa

/** Junta falas novas às que a tela já tem: sem repetir (pelo id) e em ordem de horário. */
export function mesclarMensagens(
  atuais: readonly MensagemDoChat[],
  novas: readonly MensagemDoChat[],
): MensagemDoChat[] {
  const porId = new Map<string, MensagemDoChat>();
  for (const mensagem of [...atuais, ...novas]) porId.set(mensagem.id, mensagem);
  return [...porId.values()].sort(
    (a, b) => a.criada_em.localeCompare(b.criada_em) || a.id.localeCompare(b.id),
  );
}

/** Quem fala, para o rótulo da bolha. A fala do sistema não tem autor. */
export function rotuloDoAutor(mensagem: MensagemDoChat, lado: LadoDoChat): string {
  if (mensagem.autor_tipo === "sistema") return "";
  if (mensagem.autor_tipo === "cliente") {
    return lado === "cliente" ? "Você" : (mensagem.autor_nome ?? "Cliente");
  }
  return mensagem.autor_nome ?? "Equipe";
}

/** O fato do sistema que muda o estado da conversa: a tela busca o estado de novo. */
export function mudaOEstado(mensagem: MensagemDoChat): boolean {
  return mensagem.tipo === "sistema" && Boolean(mensagem.fato) && mensagem.fato !== "fora_do_horario";
}

const formatoDeDia = new Intl.DateTimeFormat("pt-BR", { weekday: "long", hour: "2-digit", minute: "2-digit" });

/** "Voltamos segunda-feira às 09:00." — ou nada, quando o chat está aberto ou sem previsão. */
export function frasePorVoltar(proximaAbertura: string | null): string | null {
  if (!proximaAbertura) return null;
  const data = new Date(proximaAbertura);
  if (Number.isNaN(data.getTime())) return null;
  return `Voltamos ${formatoDeDia.format(data).replace(", ", " às ")}.`;
}

export function horaDaMensagem(criadaEm: string): string {
  const data = new Date(criadaEm);
  if (Number.isNaN(data.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(data);
}

/** Quanto tempo a conversa espera, em português curto: "agora", "5 min", "2 h". */
export function esperaDaConversa(desde: string, agora: Date = new Date()): string {
  const inicio = new Date(desde).getTime();
  if (Number.isNaN(inicio)) return "";
  const minutos = Math.max(0, Math.floor((agora.getTime() - inicio) / 60_000));
  if (minutos < 1) return "agora";
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return horas < 24 ? `${horas} h` : `${Math.floor(horas / 24)} d`;
}

// ----------------------------------------------------------------------------------- ao vivo

const esquemaDigitando = z.object({ quem: z.enum(["cliente", "agente"]) });

export type QuadroDaConversa =
  | { tipo: "mensagem"; mensagem: MensagemDoChat }
  | { tipo: "digitando"; quem: "cliente" | "agente" };

/** Lê um quadro SSE da conversa. Devolve `null` para o que não é do contrato. */
export function lerQuadroDaConversa(tipo: string, dados: string): QuadroDaConversa | null {
  let bruto: unknown;
  try {
    bruto = JSON.parse(dados);
  } catch {
    return null;
  }
  if (tipo === "mensagem") {
    const analise = esquemaMensagem.safeParse(bruto);
    return analise.success ? { tipo, mensagem: analise.data } : null;
  }
  if (tipo === "digitando") {
    const analise = esquemaDigitando.safeParse(bruto);
    return analise.success ? { tipo, quem: analise.data.quem } : null;
  }
  return null;
}

export const AVISOS_DA_FILA = [
  "conversa_nova",
  "mensagem_do_cliente",
  "conversa_aceita",
  "conversa_transferida",
  "conversa_encerrada",
] as const;
export type AvisoDaFila = (typeof AVISOS_DA_FILA)[number];

const esquemaAvisoDaFila = z.object({ conversa_id: uuid, ocorrido_em: z.string() });

export const FRASE_DO_AVISO: Readonly<Record<AvisoDaFila, string>> = {
  conversa_nova: "Nova conversa na fila",
  mensagem_do_cliente: "O cliente escreveu",
  conversa_aceita: "Uma conversa foi aceita",
  conversa_transferida: "Uma conversa foi transferida",
  conversa_encerrada: "Uma conversa foi encerrada",
};

export function lerAvisoDaFila(
  tipo: string,
  dados: string,
): { tipo: AvisoDaFila; conversa_id: string; ocorrido_em: string } | null {
  if (!(AVISOS_DA_FILA as readonly string[]).includes(tipo)) return null;
  let bruto: unknown;
  try {
    bruto = JSON.parse(dados);
  } catch {
    return null;
  }
  const analise = esquemaAvisoDaFila.safeParse(bruto);
  return analise.success ? { ...analise.data, tipo: tipo as AvisoDaFila } : null;
}

/** Abre um `EventSource` com os tipos pedidos e devolve a função que fecha tudo. */
function ligar(
  caminho: string,
  tipos: readonly string[],
  aoQuadro: (tipo: string, dados: string) => void,
  aoConexao?: (conectado: boolean) => void,
): () => void {
  const fonte = new EventSource(`/api${caminho}`, { withCredentials: true });
  fonte.onopen = () => aoConexao?.(true);
  fonte.onerror = () => aoConexao?.(false);
  const ouvintes = tipos.map((tipo) => {
    const ouvinte = (mensagem: Event) => aoQuadro(tipo, (mensagem as MessageEvent<string>).data);
    fonte.addEventListener(tipo, ouvinte);
    return [tipo, ouvinte] as const;
  });
  return () => {
    for (const [tipo, ouvinte] of ouvintes) fonte.removeEventListener(tipo, ouvinte);
    fonte.onopen = null;
    fonte.onerror = null;
    fonte.close();
  };
}

/**
 * Liga a conversa ao vivo enquanto `ativo`. A API reenvia o histórico desde o começo a cada
 * conexão — por isso quem consome descarta o que já tem pelo id (`mesclarMensagens`). Sem
 * `EventSource` no ambiente (navegador antigo, teste) não faz nada.
 */
export function useConversaAoVivo(
  lado: LadoDoChat,
  conversaId: string | null,
  ativo: boolean,
  aoQuadro: (quadro: QuadroDaConversa) => void,
  aoConexao?: (conectado: boolean) => void,
): void {
  const ultimoQuadro = useRef(aoQuadro);
  const ultimaConexao = useRef(aoConexao);
  useEffect(() => {
    ultimoQuadro.current = aoQuadro;
    ultimaConexao.current = aoConexao;
  });
  useEffect(() => {
    if (!ativo || !conversaId || typeof EventSource === "undefined") return;
    return ligar(
      `${caminhoDaConversa(lado, conversaId)}/eventos`,
      ["mensagem", "digitando"],
      (tipo, dados) => {
        const quadro = lerQuadroDaConversa(tipo, dados);
        if (quadro) ultimoQuadro.current(quadro);
      },
      (conectado) => ultimaConexao.current?.(conectado),
    );
  }, [lado, conversaId, ativo]);
}

/** Liga os avisos da fila de quem atende enquanto `ativo`. */
export function useFilaDoChatAoVivo(
  ativo: boolean,
  aoAviso: (aviso: { tipo: AvisoDaFila; conversa_id: string; ocorrido_em: string }) => void,
): void {
  const ultimo = useRef(aoAviso);
  useEffect(() => {
    ultimo.current = aoAviso;
  });
  useEffect(() => {
    if (!ativo || typeof EventSource === "undefined") return;
    return ligar("/webchat/atendimento/eventos", AVISOS_DA_FILA, (tipo, dados) => {
      const aviso = lerAvisoDaFila(tipo, dados);
      if (aviso) ultimo.current(aviso);
    });
  }, [ativo]);
}

// ----------------------------------------------------------------------------- consultas e ações

function invalidar(cliente: QueryClient) {
  void cliente.invalidateQueries({ queryKey: CHAVE_DO_WEBCHAT });
}

export function useEstadoDoChat(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DO_WEBCHAT, "estado"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/webchat/estado", esquemaEstadoDoChat, { signal }),
  });
}

export function useMensagensDaConversa(lado: LadoDoChat, conversaId: string | null) {
  return useQuery({
    queryKey: [...CHAVE_DO_WEBCHAT, "mensagens", lado, conversaId],
    enabled: Boolean(conversaId),
    queryFn: ({ signal }) =>
      pedirJson(`${caminhoDaConversa(lado, conversaId ?? "")}/mensagens`, z.array(esquemaMensagem), {
        signal,
      }),
  });
}

/** Põe uma fala (do SSE ou da resposta do POST) no cache da conversa, sem repetir. */
export function acrescentarMensagem(
  cliente: QueryClient,
  lado: LadoDoChat,
  conversaId: string,
  mensagem: MensagemDoChat,
) {
  cliente.setQueryData<MensagemDoChat[]>(
    [...CHAVE_DO_WEBCHAT, "mensagens", lado, conversaId],
    (atuais) => mesclarMensagens(atuais ?? [], [mensagem]),
  );
}

export function useAbrirConversa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (texto: string) =>
      enviarJson("/webchat/conversas", esquemaConversaDoCliente, { texto }),
    onSuccess: () => invalidar(cliente),
  });
}

export function useEnviarMensagem(lado: LadoDoChat, conversaId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (texto: string) =>
      enviarJson(`${caminhoDaConversa(lado, conversaId)}/mensagens`, esquemaMensagem, { texto }),
    onSuccess: (mensagem) => acrescentarMensagem(cliente, lado, conversaId, mensagem),
  });
}

/** O sinal de "digitando": falha em silêncio, porque é só um detalhe da conversa. */
export function sinalizarDigitando(lado: LadoDoChat, conversaId: string): Promise<void> {
  return enviarSemConteudo(`${caminhoDaConversa(lado, conversaId)}/digitando`).catch(() => undefined);
}

export function useCancelarConversa(conversaId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      enviarJson(`${caminhoDaConversa("cliente", conversaId)}/cancelamento`, esquemaConversaDoCliente, {}),
    onSuccess: () => invalidar(cliente),
  });
}

export function useAvaliarConversa(conversaId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { nota: number | null; comentario: string }) =>
      enviarJson(`${caminhoDaConversa("cliente", conversaId)}/avaliacao`, esquemaConversaDoCliente, {
        nota: corpo.nota,
        comentario: corpo.comentario.trim() || null,
      }),
    onSuccess: () => invalidar(cliente),
  });
}

// ------------------------------------------------------------------------------------ atendimento

export function useFilaDoChat(
  filtros: { equipeId?: string; encerradas?: boolean } = {},
  habilitado = true,
) {
  const parametros = new URLSearchParams();
  if (filtros.equipeId) parametros.set("equipe_id", filtros.equipeId);
  if (filtros.encerradas) parametros.set("encerradas", "true");
  const consulta = parametros.toString();
  return useQuery({
    queryKey: [...CHAVE_DO_WEBCHAT, "fila", filtros.equipeId ?? "", Boolean(filtros.encerradas)],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(
        `/webchat/atendimento/fila${consulta ? `?${consulta}` : ""}`,
        z.array(esquemaConversaDoAtendimento),
        { signal },
      ),
  });
}

/** Os chamados da empresa da conversa: é entre eles que a transcrição pode ser anexada. */
export function useChamadosDaOrganizacao(organizacaoId: string) {
  return useQuery({
    queryKey: ["tickets", "da-organizacao", organizacaoId],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets?organizacao_id=${encodeURIComponent(organizacaoId)}`, z.array(esquemaTicket), {
        signal,
      }),
  });
}

export function useAceitarConversa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`${caminhoDaConversa("atendimento", id)}/aceite`, esquemaConversaDoAtendimento, {}),
    onSuccess: () => invalidar(cliente),
  });
}

export function useTransferirConversa(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (destino: { atendenteId?: string; equipeId?: string }) =>
      enviarJson(`${caminhoDaConversa("atendimento", id)}/transferencia`, esquemaConversaDoAtendimento, {
        atendente_id: destino.atendenteId ?? null,
        equipe_id: destino.equipeId ?? null,
      }),
    onSuccess: () => invalidar(cliente),
  });
}

export interface FormularioDeEncerramento {
  modo: "novo_chamado" | "anexar";
  assunto: string;
  urgencia: "baixa" | "media" | "alta" | "urgente";
  ticketId: string;
}

export const ENCERRAMENTO_EM_BRANCO: FormularioDeEncerramento = {
  modo: "novo_chamado",
  assunto: "",
  urgencia: "media",
  ticketId: "",
};

export function validarEncerramento(form: FormularioDeEncerramento): string | null {
  if (form.modo === "novo_chamado" && !form.assunto.trim()) {
    return "Dê um assunto ao chamado que nasce desta conversa.";
  }
  if (form.modo === "anexar" && !form.ticketId) {
    return "Escolha o chamado em que a conversa vai entrar.";
  }
  return null;
}

export function corpoDoEncerramento(form: FormularioDeEncerramento): Record<string, unknown> {
  return form.modo === "novo_chamado"
    ? { modo: "novo_chamado", assunto: form.assunto.trim(), urgencia: form.urgencia }
    : { modo: "anexar", ticket_id: form.ticketId };
}

export function useEncerrarConversa(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (form: FormularioDeEncerramento) =>
      enviarJson(
        `${caminhoDaConversa("atendimento", id)}/encerramento`,
        esquemaResultadoDoEncerramento,
        corpoDoEncerramento(form),
      ),
    onSuccess: () => {
      invalidar(cliente);
      void cliente.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}

// ------------------------------------------------------------------------------------ configuração

export function useConfiguracaoDoChat(organizacaoId: string) {
  return useQuery({
    queryKey: [...CHAVE_DO_WEBCHAT, "configuracao", organizacaoId],
    enabled: Boolean(organizacaoId),
    queryFn: ({ signal }) =>
      pedirJson(`/webchat/configuracoes/${encodeURIComponent(organizacaoId)}`, esquemaConfiguracao, {
        signal,
      }),
  });
}

export interface FormularioDeConfiguracao {
  ativo: boolean;
  calendarioId: string;
  equipeId: string;
  mensagem: string;
  digitando: boolean;
}

export function formularioDaConfiguracao(conf: ConfiguracaoDoChat): FormularioDeConfiguracao {
  return {
    ativo: conf.ativo,
    calendarioId: conf.calendario_id ?? "",
    equipeId: conf.equipe_id ?? "",
    mensagem: conf.mensagem_fora_do_horario ?? "",
    digitando: conf.digitando_ativo,
  };
}

export function corpoDaConfiguracao(form: FormularioDeConfiguracao): Record<string, unknown> {
  return {
    ativo: form.ativo,
    calendario_id: form.calendarioId || null,
    equipe_id: form.equipeId || null,
    mensagem_fora_do_horario: form.mensagem.trim() || null,
    digitando_ativo: form.digitando,
  };
}

export function useConfigurarChat(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (form: FormularioDeConfiguracao) =>
      atualizarJson(
        `/webchat/configuracoes/${encodeURIComponent(organizacaoId)}`,
        esquemaConfiguracao,
        corpoDaConfiguracao(form),
      ),
    onSuccess: () => invalidar(cliente),
  });
}
